import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Eye,
  Inbox,
  Megaphone,
  MousePointerClick,
  RefreshCw,
  TrendingUp,
  TriangleAlert,
  Users,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { ResolvedPeriodView } from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { DailySpendChart } from "@/components/crm/DailySpendChart";
import { PeriodPicker } from "@/components/crm/PeriodPicker";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Stat } from "@/components/ui/stat";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, ApiError } from "@/lib/api";
import { formatCurrency, formatDateTime, localDateInput, relativeTime } from "@/lib/crm";
import { usePeriod } from "@/lib/period";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/marketing")({
  component: MarketingPage,
});

type Row = {
  id: string | null;
  externalId: string | null;
  name: string;
  spend: number;
  impressions: number;
  clicks: number;
  platformConversions: number;
  leads: number;
  won: number;
  revenue: number;
  cpl: number | null;
  cac: number | null;
  roas: number | null;
};

type Overview = {
  level: string;
  rows: Row[];
  totals: {
    spend: number;
    leads: number;
    revenue: number;
    won: number;
    cpl: number | null;
    roas: number | null;
  };
};

type WebhookEvent = {
  id: string;
  provider: string;
  objectType: string | null;
  status: string;
  signatureValid: boolean;
  attempts: number;
  error: string | null;
  receivedAt: string;
  processedAt: string | null;
};

const inteiro = (v: number) => v.toLocaleString("pt-BR");

/**
 * Marketing.
 *
 * A tabela põe lado a lado o que a Meta reporta e o que o CRM registrou. A
 * diferença entre as duas colunas é o ponto: a plataforma conta atribuição por
 * visualização, o CRM conta dinheiro que entrou. Mostrar só uma delas seria
 * escolher entre otimismo e desconfiança sem dar a informação para decidir.
 */
function MarketingPage() {
  const [level, setLevel] = useState<"campaign" | "adset" | "ad">("campaign");
  const { query, key } = usePeriod();
  const queryClient = useQueryClient();

  const overview = useQuery({
    queryKey: ["marketing", level, key],
    queryFn: () => api.get<Overview>("/marketing/overview", { level, ...query }),
  });

  // Mesma consulta e mesma chave do PeriodPicker: o React Query devolve a
  // resposta que ele já buscou, sem outra ida ao servidor.
  const periodo = useQuery({
    queryKey: ["periods", "resolve", key],
    queryFn: () => api.get<ResolvedPeriodView>("/periods/resolve", query),
    staleTime: 60_000,
  });

  const sync = useMutation({
    mutationFn: async () => {
      await api.post("/marketing/sync/structure");
      return api.post<{ rows: number }>(`/marketing/sync/spend?days=${janelaDeSync(periodo.data)}`);
    },
    onSuccess: async (r) => {
      toast.success(`${r.rows} linha(s) de investimento atualizadas.`);
      await queryClient.invalidateQueries({ queryKey: ["marketing"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Não foi possível sincronizar"),
  });

  const totais = overview.data?.totals;
  const linhas = overview.data?.rows ?? [];

  // Cliques e impressões vêm somados das linhas: a API só totaliza gasto,
  // leads e receita, e somar aqui poupa uma segunda chamada por dois números.
  // O CAC é gasto sobre vendas — sem venda não há divisão, e "—" é mais
  // honesto do que zero.
  const cliques = linhas.reduce((n, r) => n + r.clicks, 0);
  const impressoes = linhas.reduce((n, r) => n + r.impressions, 0);
  const ctr = impressoes > 0 ? (cliques / impressoes) * 100 : null;
  const cac = totais && totais.won > 0 ? totais.spend / totais.won : null;
  const roas = totais?.roas ?? null;

  return (
    <>
      <PageHeader
        title="Marketing"
        description="O que foi investido e o que voltou"
        action={
          <>
            <PeriodPicker />
            <Button
              variant="outline"
              size="sm"
              disabled={sync.isPending}
              onClick={() => sync.mutate()}
            >
              <RefreshCw className={cn(sync.isPending && "animate-spin")} />
              {sync.isPending ? "Sincronizando…" : "Sincronizar"}
            </Button>
          </>
        }
      />

      <PageBody>
        <Tabs defaultValue="desempenho">
          <TabsList>
            <TabsTrigger value="desempenho">Desempenho</TabsTrigger>
            <TabsTrigger value="webhooks">Entrada de dados</TabsTrigger>
          </TabsList>

          <TabsContent value="desempenho" className="space-y-5">
            <MetaSyncStatus />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Stat
                label="Investido"
                value={formatCurrency(totais?.spend ?? 0)}
                icon={Megaphone}
                tone="primary"
                loading={overview.isPending}
                hint={`${inteiro(totais?.leads ?? 0)} lead(s) no CRM`}
              />
              <Stat
                label="Cliques"
                value={inteiro(cliques)}
                icon={MousePointerClick}
                loading={overview.isPending}
                hint={ctr === null ? "sem impressões no período" : `CTR ${ctr.toFixed(2)}%`}
              />
              <Stat
                label="Impressões"
                value={inteiro(impressoes)}
                icon={Eye}
                loading={overview.isPending}
              />
              <Stat
                label="CPL"
                value={totais?.cpl ? formatCurrency(totais.cpl) : "—"}
                icon={Users}
                loading={overview.isPending}
                hint="custo por lead que entrou no CRM"
              />
              <Stat
                label="CAC"
                value={cac === null ? "—" : formatCurrency(cac)}
                icon={Wallet}
                loading={overview.isPending}
                hint={`${inteiro(totais?.won ?? 0)} venda(s) paga(s)`}
              />
              <Stat
                label="ROAS"
                value={roas ? `${roas.toFixed(2)}x` : "—"}
                icon={TrendingUp}
                tone={roas !== null && roas > 0 && roas < 1 ? "danger" : "default"}
                loading={overview.isPending}
                hint={`receita paga ${formatCurrency(totais?.revenue ?? 0)}`}
              />
            </div>

            {/* Os totais do gráfico já estão nos números acima. */}
            <DailySpendChart showTotals={false} />

            <Card>
              <CardHeader>
                <div className="min-w-0">
                  <CardTitle>Meta × CRM</CardTitle>
                  <CardDescription>
                    <strong className="font-medium text-foreground">Conv. Meta</strong> é o que a
                    plataforma relata;{" "}
                    <strong className="font-medium text-foreground">Leads CRM</strong> é o que
                    chegou de fato. A diferença é normal — a Meta conta atribuição por visualização.
                  </CardDescription>
                </div>
                <Select
                  value={level}
                  onChange={(e) => setLevel(e.target.value as typeof level)}
                  aria-label="Nível"
                  className="w-40 [&>select]:h-8 [&>select]:text-xs"
                >
                  <option value="campaign">Por campanha</option>
                  <option value="adset">Por conjunto</option>
                  <option value="ad">Por anúncio</option>
                </Select>
              </CardHeader>

              <CardContent className="px-0 pb-0 pt-3">
                {overview.isPending ? (
                  <div className="space-y-2 px-5 pb-5">
                    {Array.from({ length: 5 }, (_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : linhas.length === 0 ? (
                  <EmptyState
                    compact
                    icon={Megaphone}
                    title="Nenhum investimento no período"
                    description="Configure o Meta Ads em Configurações › Integrações e sincronize."
                    className="pb-8"
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[56rem] text-sm">
                      <thead className="bg-muted/60 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-5 py-2.5 text-left">Nome</th>
                          <th className="px-4 py-2.5 text-right">Investido</th>
                          <th className="px-4 py-2.5 text-right" title="O que a Meta relata">
                            Conv. Meta
                          </th>
                          <th className="px-4 py-2.5 text-right" title="O que o CRM registrou">
                            Leads CRM
                          </th>
                          <th className="px-4 py-2.5 text-right">CPL</th>
                          <th className="px-4 py-2.5 text-right">Vendas</th>
                          <th className="px-4 py-2.5 text-right">Receita paga</th>
                          <th className="px-5 py-2.5 text-right">ROAS</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {linhas.map((row) => (
                          <tr
                            key={row.id ?? row.name}
                            className="transition-colors hover:bg-muted/40"
                          >
                            <td className="px-5 py-2.5">
                              <span
                                className="block max-w-[18rem] truncate font-medium"
                                title={row.name}
                              >
                                {row.name}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums">
                              {formatCurrency(row.spend)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                              {inteiro(row.platformConversions)}
                            </td>
                            <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                              {inteiro(row.leads)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums">
                              {row.cpl ? formatCurrency(row.cpl) : "—"}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums">
                              {inteiro(row.won)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums">
                              {row.revenue > 0 ? formatCurrency(row.revenue) : "—"}
                            </td>
                            <td className="px-5 py-2.5 text-right">
                              <Roas value={row.roas} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>

              <CardFooter>
                <p>
                  O ROAS usa{" "}
                  <strong className="font-medium text-foreground">somente receita paga</strong>,
                  nunca venda a receber.
                </p>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="webhooks">
            <WebhookDiagnostics />
          </TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

/**
 * ROAS como etiqueta. Só entra quando existe: a API devolve nulo (ou zero)
 * quando não há receita paga para dividir, e "—" é o que a tela sempre
 * mostrou nesse caso.
 */
function Roas({ value }: { value: number | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant={value < 1 ? "danger" : "success"} className="tabular-nums">
      {value.toFixed(2)}x
    </Badge>
  );
}

/**
 * Quantos dias pedir à Meta no "Sincronizar".
 *
 * Sincronizar é uma janela para trás a partir de hoje, e não um filtro de
 * relatório — por isso continua em `days`, e não com o período da tela. A
 * janela vai do início do período escolhido até hoje: "mês passado" visto no
 * dia 10 precisa de uns 40 dias, e não dos 31 do mês, senão o começo dele
 * fica sem custo.
 *
 * Teto de 90: acima disso a chamada fica lenta e pesa no limite de uso da API
 * da Meta, e 90 dias é também o histórico que a primeira sincronização da
 * conta já traz sozinha. Sem o período resolvido (carregando ou inválido), 30
 * — o padrão que a tela usava antes.
 */
function janelaDeSync(periodo: ResolvedPeriodView | undefined): number {
  if (!periodo) return 30;
  // "Hoje" aqui é o do navegador, que pode estar um dia atrás do da clínica:
  // o dia de folga cobre isso, e sincronizar um dia a mais não custa nada — a
  // gravação do investimento é idempotente.
  const hoje = localDateInput(new Date());
  const dias = Math.round((Date.parse(hoje) - Date.parse(periodo.fromDay)) / 86_400_000) + 2;
  return Number.isFinite(dias) ? Math.min(90, Math.max(1, dias)) : 30;
}

/** Situação do evento de entrada → rótulo e etiqueta do tema. */
const SITUACAO: Record<string, { label: string; variant: NonNullable<BadgeProps["variant"]> }> = {
  processed: { label: "Processado", variant: "success" },
  queued: { label: "Na fila", variant: "info" },
  failed: { label: "Falhou", variant: "danger" },
  dead_letter: { label: "Dead letter", variant: "danger" },
};

/**
 * Entrada de dados.
 *
 * Sem esta aba, um evento que morreu na dead letter fica no banco e ninguém
 * vê — o lead simplesmente não apareceu, e a investigação começa procurando
 * erro na campanha em vez de na fila.
 */
function WebhookDiagnostics() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");

  const events = useQuery({
    queryKey: ["webhook-events", status],
    queryFn: () =>
      api.get<{ data: WebhookEvent[]; byStatus: Record<string, number> }>(
        "/marketing/webhook-events",
        { status: status || undefined, pageSize: 50 },
      ),
    refetchInterval: 20_000,
  });

  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/marketing/webhook-events/${id}/retry`),
    onSuccess: async () => {
      toast.success("Reenfileirado.");
      await queryClient.invalidateQueries({ queryKey: ["webhook-events"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Não foi possível reprocessar"),
  });

  const contagem = events.data?.byStatus ?? {};
  const mortos = contagem["dead_letter"] ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>Eventos recebidos</CardTitle>
          <CardDescription>
            Webhooks da Meta e do WhatsApp, com o que aconteceu com cada um. Evento que morreu na
            fila aparece aqui — e não como lead que sumiu.
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {mortos > 0 && (
            <Badge variant="danger">
              <TriangleAlert />
              {mortos} desistiram depois de todas as tentativas
            </Badge>
          )}
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Situação"
            className="w-44 [&>select]:h-8 [&>select]:text-xs"
          >
            <option value="">Todos</option>
            <option value="processed">Processados ({contagem["processed"] ?? 0})</option>
            <option value="queued">Na fila ({contagem["queued"] ?? 0})</option>
            <option value="failed">Falharam ({contagem["failed"] ?? 0})</option>
            <option value="dead_letter">Dead letter ({mortos})</option>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="px-0 pb-0 pt-3">
        {events.isPending ? (
          <div className="space-y-2 px-5 pb-5">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (events.data?.data.length ?? 0) === 0 ? (
          <EmptyState
            compact
            icon={Inbox}
            title="Nenhum evento recebido ainda"
            description="Assim que a Meta ou o WhatsApp mandarem algo, ele aparece aqui com a situação."
            className="pb-8"
          />
        ) : (
          <div className="overflow-x-auto rounded-b-xl">
            <table className="w-full min-w-[48rem] text-sm">
              <thead className="bg-muted/60 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-5 py-2.5 text-left">Origem</th>
                  <th className="px-4 py-2.5 text-left">Tipo</th>
                  <th className="px-4 py-2.5 text-left">Situação</th>
                  <th className="px-4 py-2.5 text-left">Recebido</th>
                  <th className="px-4 py-2.5 text-left">Erro</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {events.data?.data.map((e) => {
                  const situacao = SITUACAO[e.status] ?? {
                    label: e.status,
                    variant: "muted" as const,
                  };
                  return (
                    <tr key={e.id} className="transition-colors hover:bg-muted/40">
                      <td className="px-5 py-2.5 font-medium">
                        {e.provider}
                        {!e.signatureValid && (
                          <span
                            className="ml-1.5 text-xs font-normal text-muted-foreground"
                            title="Canal sem assinatura HMAC"
                          >
                            (sem assinatura)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{e.objectType ?? "—"}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={situacao.variant}>{situacao.label}</Badge>
                        {e.attempts > 1 && (
                          <span className="ml-1.5 text-[11px] tabular-nums text-muted-foreground">
                            {e.attempts} tentativas
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-xs tabular-nums text-muted-foreground">
                        {formatDateTime(e.receivedAt)}
                      </td>
                      <td
                        className="max-w-64 truncate px-4 py-2.5 text-xs text-destructive"
                        title={e.error ?? undefined}
                      >
                        {e.error ?? ""}
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        {(e.status === "failed" || e.status === "dead_letter") && (
                          <Button
                            variant="outline"
                            size="xs"
                            disabled={retry.isPending}
                            onClick={() => retry.mutate(e.id)}
                          >
                            <RefreshCw />
                            Reprocessar
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type IntegrationRow = {
  provider: string;
  status: string;
  lastSyncAt: string | null;
  lastError: string | null;
  missingSecrets: string[];
  config: Record<string, unknown>;
};

/**
 * Estado da conexão com a Meta.
 *
 * Linha fixa, e não um aviso que some. Quando a sincronização quebra, os
 * números da tela continuam ali — os da última vez que deu certo — e nada
 * indica que estão velhos. Uma tabela desatualizada com cara de atual é pior
 * do que uma tabela vazia: dá para tomar decisão de verba em cima dela.
 */
function MetaSyncStatus() {
  const integracoes = useQuery({
    queryKey: ["integrations"],
    queryFn: () => api.get<IntegrationRow[]>("/integrations"),
  });

  const meta = integracoes.data?.find((i) => i.provider === "meta_ads");
  if (!meta) return null;

  const contaFaltando = !meta.config["adAccountId"];
  const tokenFaltando = meta.missingSecrets.includes("access_token");

  if (contaFaltando || tokenFaltando) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-dashed border-warning/60 bg-warning/10 px-4 py-3 text-sm">
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-warning/18 text-warning-foreground dark:text-warning">
          <TriangleAlert className="size-4" />
        </span>
        <p className="text-muted-foreground">
          Meta Ads ainda não conectada — falta{" "}
          {[contaFaltando && "o ID da conta", tokenFaltando && "o token"]
            .filter(Boolean)
            .join(" e ")}
          . Configure em{" "}
          <strong className="font-medium text-foreground">Configurações › Integrações</strong>. Sem
          isso, o custo não entra e o ROAS desta tela fica sem denominador.
        </p>
      </div>
    );
  }

  const comErro = meta.status === "error" && Boolean(meta.lastError);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-4 py-2.5 text-xs",
        comErro ? "border-destructive/30 bg-destructive/10" : "bg-card shadow-card",
      )}
    >
      <span className="inline-flex items-center gap-2 font-medium">
        <span
          aria-hidden
          className={cn("size-2 rounded-full", comErro ? "bg-destructive" : "bg-success")}
        />
        {comErro ? "Sincronização falhou" : "Meta Ads conectada"}
      </span>
      <span className="text-muted-foreground">
        conta {String(meta.config["adAccountId"] ?? "—")}
      </span>
      <span className="text-muted-foreground">
        {meta.lastSyncAt
          ? `última atualização ${relativeTime(meta.lastSyncAt)}`
          : "ainda não sincronizou"}
      </span>
      {comErro && <span className="w-full text-destructive">{meta.lastError}</span>}
    </div>
  );
}
